import React, { useCallback } from "react";
import { useSelector } from "react-redux";

import _ from "lodash";

import {
  Card,
  CardContent,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@material-ui/core";

import clsx from "clsx";

import { DashboardState } from "../reducers/dashboard";
import { useDestinationListStyles } from "../components/DestinationsList";
import { WarehouseDestinationSlug } from "../destinations/types";
import {
  WarehouseDestination,
  warehouseDestinations,
} from "../destinations/destinations";
import { LocalStorageEnvironment } from "../reducers/environments";

export const SetupNewWarehouseList: React.FC = () => {
  const { data: dashboardData, loading } = useSelector(
    (state: { dashboard: DashboardState }) => state.dashboard
  );
  const currentEnvironment = useSelector(
    (state: {
      environments: { currentEnvironment: LocalStorageEnvironment };
    }) => {
      return state.environments.currentEnvironment;
    }
  );

  const classes = useDestinationListStyles();
  const alreadySetupWarehouseKeys: Partial<Record<
    WarehouseDestinationSlug,
    boolean
  >> = {};
  dashboardData.forEach(
    (warehouseStatus) =>
      (alreadySetupWarehouseKeys[
        warehouseStatus.warehouse_destination.slug
      ] = true)
  );

  let filteredDestinations = _.chain(warehouseDestinations)
    .values()
    .filter((dest) => !alreadySetupWarehouseKeys[dest.slug])
    .sortBy((dest) => dest.slug)
    .value();

  const simpleDatabaseFields = [
    "Host: ",
    "Port: ",
    "Database Name: ",
    "Database User: ",
    "DB is publicly accessible (y/n): ",
  ];
  const databaseWithPasswordFields = [
    ...simpleDatabaseFields,
    "Database Password: ",
  ];

  const warehouseFields: Record<WarehouseDestinationSlug, string[]> = {
    azure: databaseWithPasswordFields,
    panoply: databaseWithPasswordFields,
    postgres: databaseWithPasswordFields,
    bigquery: [
      "Project ID: ",
      "Data Location: US, EU, US_WEST_2, US_EAST_4, etc",
    ],
    databricks: [],
    snowflake: [...simpleDatabaseFields, "Password or Private Key: ", "Role: "],
    redshift: [
      ...simpleDatabaseFields,
      "Password or IAM Role ARN: ",
      "AWS Region: ",
    ],
    s3: ["AWS Account ID: ", "External ID: (optional)"],
  };

  const getDestinationMailToLink = useCallback(
    (dest: WarehouseDestination): string => {
      if (currentEnvironment) {
        const fields = warehouseFields[dest.slug];
        const fieldsText =
          fields.length > 0
            ? `The the warehouse connection info is: \n\n${fields.join("\n")}`
            : "";
        const body = `Hi, I'd like to set up a new ${dest.name} warehouse, connected to env: ${currentEnvironment.ID}. ${fieldsText}\n\nThanks`;

        const subject = `Setting up ${dest.name} destination`;
        return `mailto:warehouses@freshpaint.io?subject=${encodeURI(
          subject
        )}&body=${encodeURI(body)}`;
      }

      return "";
    },
    [warehouseFields, currentEnvironment]
  );

  if (loading) {
    return null;
  }

  return (
    <Card
      className={clsx("flex-shrink-0 mb-5", {
        "w-full 2xl:w-4/12": Object.keys(alreadySetupWarehouseKeys).length > 0,
        "w-3/4": Object.keys(alreadySetupWarehouseKeys).length === 0,
      })}
    >
      <CardContent className={classes.content}>
        <div key="search-wrapper" className={classes.cardHeader}>
          <h4 className="font-bold">
            {Object.keys(alreadySetupWarehouseKeys).length > 0
              ? "Connect Other warehouses"
              : "Warehouses"}
          </h4>
        </div>

        <Divider />
        <div>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Integration</TableCell>
                <TableCell>&nbsp;</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredDestinations.map((dest) => {
                const DestinationIcon = dest.logoSquare;

                return (
                  <TableRow key={dest.key}>
                    <TableCell
                      className={clsx(classes.tableCell, "cursor-pointer")}
                    >
                      <div className={clsx(classes.destinationInfo)}>
                        {DestinationIcon && (
                          <DestinationIcon height={34} width={34} />
                        )}
                        <span className={classes.destinationName}>
                          {dest.name}
                        </span>
                      </div>
                    </TableCell>
                    {/* TODO: replace mail link with Freshdesk's form when that is available */}
                    <TableCell align="right" className={classes.tableCell}>
                      <a
                        className="text-xs bg-navy-50 text-navy-800 hover:bg-navy-200 px-2 py-2 rounded"
                        href={getDestinationMailToLink(dest)}
                        rel="noreferrer noopener"
                        target="_blank"
                      >
                        Contact Us
                      </a>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};
